/**
 * <skin-concern-marquee>
 * Carrusel horizontal continuo para mobile (<750px): autoplay lento, movimiento
 * continuo pixel a pixel, arrastre libre con Pointer Events (sin scroll-snap),
 * inercia simple al soltar, loop infinito visual mediante clones aria-hidden,
 * pausa en prefers-reduced-motion. En desktop (>=750px) no hace nada: el <ul>
 * interno se muestra como grid estático.
 *
 * NOTA TÉCNICA IMPORTANTE (por qué setInterval y no requestAnimationFrame):
 * Se depuró exhaustivamente esta sección con requestAnimationFrame como motor
 * de movimiento y, pese a que la lógica se demostró matemáticamente correcta
 * en cada prueba aislada, el movimiento automático seguía sin apreciarse en
 * dispositivo real. requestAnimationFrame tiene un comportamiento documentado
 * de limitarse o suspenderse de forma agresiva e impredecible en navegadores
 * móviles bajo ciertas condiciones de ahorro de energía (p.ej. Modo de bajo
 * consumo en iOS), incluso con la pestaña en primer plano. setInterval con
 * delta de tiempo real (no un contador de frames fijo) consigue el mismo
 * movimiento continuo y pixel a pixel sin depender de ese mecanismo.
 */
class SkinConcernMarquee extends HTMLElement {
  constructor() {
    super();
    this.SPEED_PX_PER_SEC = 20;
    this.TICK_MS = 50;
    this.RESUME_DELAY_MS = 900;
    this.FRICTION_PER_TICK = 0.85;
    this.MIN_VELOCITY = 0.02;
    this.DRAG_THRESHOLD = 6;
    this.MOBILE_BREAKPOINT = 749;
    this.STUCK_PAUSE_MS = 4000;

    this.enabled = false;
    this.cloned = false;
    this.loopWidth = 0;
    this.pointerActive = false;
    this.isDragging = false;
    this.autoplayPaused = false;
    this.pausedAt = null;
    this.intervalId = null;
    this.momentumIntervalId = null;
    this.resumeTimeout = null;
    this.watchdogId = null;
    this.resizeTimeout = null;
    this.lastTickAt = null;
    this.pointerStartX = 0;
    this.scrollStart = 0;
    this.velocityTracker = [];

    this.tick = this.tick.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-marquee-track]');
    if (!this.track) {
      console.warn('[skin-concern-marquee] no se encontró el track (data-marquee-track); el carrusel no se inicializa.');
      return;
    }

    this.track.style.overflowAnchor = 'none';

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('resize', this.onResize);

    // Comprobación síncrona e inmediata: window.innerWidth está disponible
    // desde el primer instante (el viewport ya está establecido por la
    // etiqueta meta viewport antes de que se ejecute cualquier script), así
    // que no hace falta ningún retraso ni depender de matchMedia.
    this.syncWithBreakpoint();
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimeout);
    this.disable();
  }

  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.syncWithBreakpoint(), 150);
  }

  syncWithBreakpoint() {
    if (window.innerWidth <= this.MOBILE_BREAKPOINT) {
      this.enable();
    } else {
      this.disable();
    }
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.cloneItems();
    this.track.addEventListener('pointerdown', this.onPointerDown);
    if (this.reduceMotion) {
      console.info('[skin-concern-marquee] autoplay desactivado: prefers-reduced-motion está activo en este navegador/dispositivo.');
    } else {
      this.startLoop();
      console.info('[skin-concern-marquee] autoplay móvil iniciado (motor: setInterval).');
    }
    this.startWatchdog();
  }

  disable() {
    const wasEnabled = this.enabled;
    this.enabled = false;
    this.stopLoop();
    this.stopWatchdog();
    this.stopMomentum();
    clearTimeout(this.resumeTimeout);
    if (wasEnabled && this.track) {
      this.track.removeEventListener('pointerdown', this.onPointerDown);
    }
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.isDragging = false;
    this.pointerActive = false;
    this.autoplayPaused = false;
    this.pausedAt = null;
    if (this.track) this.track.scrollLeft = 0;
    this.removeClones();
  }

  cloneItems() {
    if (this.cloned) return;
    const items = Array.from(this.track.children);
    items.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      this.track.appendChild(clone);
    });
    this.loopWidth = this.track.scrollWidth / 2;
    this.cloned = true;
  }

  removeClones() {
    if (!this.cloned || !this.track) return;
    const items = Array.from(this.track.children);
    const half = Math.floor(items.length / 2);
    items.slice(half).forEach((el) => el.remove());
    this.cloned = false;
    this.loopWidth = 0;
  }

  normalizeLoop() {
    if (!this.cloned || !this.loopWidth) return;
    if (this.track.scrollLeft >= this.loopWidth) {
      this.track.scrollLeft -= this.loopWidth;
    } else if (this.track.scrollLeft < 0) {
      this.track.scrollLeft += this.loopWidth;
    }
  }

  onPointerDown(event) {
    if (!this.enabled) return;
    // No pausamos el autoplay ni tocamos scrollLeft todavía: hasta que el
    // movimiento supere DRAG_THRESHOLD esto se trata como un posible tap,
    // para no "robarle" el click nativo a la tarjeta (bug real: un micro-scroll
    // durante un toque hace que el navegador cancele el evento click sintético).
    this.pointerActive = true;
    this.isDragging = false;
    this.pointerStartX = event.clientX;
    this.scrollStart = this.track.scrollLeft;
    this.velocityTracker = [{ x: event.clientX, t: performance.now() }];

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.pointerActive) return;
    const dx = event.clientX - this.pointerStartX;

    if (!this.isDragging) {
      if (Math.abs(dx) < this.DRAG_THRESHOLD) return;
      // Umbral superado: esto ya es un arrastre real, no un tap.
      this.isDragging = true;
      this.pauseAutoplay();
      this.stopMomentum();
    }

    this.track.scrollLeft = this.scrollStart - dx;
    this.velocityTracker.push({ x: event.clientX, t: performance.now() });
    if (this.velocityTracker.length > 5) this.velocityTracker.shift();
    this.normalizeLoop();
  }

  onPointerUp() {
    if (!this.pointerActive) return;
    this.pointerActive = false;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);

    if (this.isDragging) {
      this.isDragging = false;
      this.applyMomentum(this.computeVelocity());
    }
    // Si nunca superó el umbral (isDragging seguía false), no se pausó el
    // autoplay ni se tocó scrollLeft: el navegador dispara el click nativo
    // de la tarjeta con normalidad.
  }

  computeVelocity() {
    if (this.velocityTracker.length < 2) return 0;
    const first = this.velocityTracker[0];
    const last = this.velocityTracker[this.velocityTracker.length - 1];
    const dt = last.t - first.t;
    if (dt <= 0) return 0;
    return (last.x - first.x) / dt; // px por ms
  }

  applyMomentum(v) {
    // px por tick (TICK_MS), invertido: arrastrar hacia la izquierda debe
    // continuar desplazando el scroll en la misma dirección que llevaba.
    let velocityPerTick = -v * this.TICK_MS;

    if (Math.abs(velocityPerTick) <= this.MIN_VELOCITY) {
      this.scheduleResume();
      return;
    }

    this.stopMomentum();
    this.momentumIntervalId = setInterval(() => {
      if (Math.abs(velocityPerTick) < this.MIN_VELOCITY || this.isDragging) {
        this.stopMomentum();
        this.scheduleResume();
        return;
      }
      this.track.scrollLeft += velocityPerTick;
      velocityPerTick *= this.FRICTION_PER_TICK;
      this.normalizeLoop();
    }, this.TICK_MS);
  }

  stopMomentum() {
    if (this.momentumIntervalId) {
      clearInterval(this.momentumIntervalId);
      this.momentumIntervalId = null;
    }
  }

  pauseAutoplay() {
    this.autoplayPaused = true;
    this.pausedAt = Date.now();
    clearTimeout(this.resumeTimeout);
    this.stopLoop();
  }

  scheduleResume() {
    clearTimeout(this.resumeTimeout);
    this.resumeTimeout = setTimeout(() => {
      this.autoplayPaused = false;
      this.pausedAt = null;
      if (this.enabled && !this.reduceMotion) {
        this.startLoop();
      }
    }, this.RESUME_DELAY_MS);
  }

  startLoop() {
    if (this.intervalId || this.reduceMotion || !this.enabled || this.autoplayPaused || this.isDragging) return;
    this.lastTickAt = Date.now();
    this.intervalId = setInterval(this.tick, this.TICK_MS);
  }

  stopLoop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Red de seguridad: si por cualquier motivo el autoplay debería estar
  // corriendo pero no hay ningún intervalo activo, lo reinicia. Coste
  // mínimo (una comprobación cada 2.5s), nunca interfiere mientras se
  // arrastra o está en una pausa real y reciente.
  startWatchdog() {
    if (this.watchdogId) return;
    this.watchdogId = setInterval(() => {
      // Si la pausa lleva mas de STUCK_PAUSE_MS sin interaccion activa, la
      // tratamos como bloqueada (p.ej. si la inercia se interrumpio antes de
      // completarse) y forzamos la reanudacion.
      if (
        this.autoplayPaused &&
        !this.isDragging &&
        !this.pointerActive &&
        this.pausedAt &&
        Date.now() - this.pausedAt > this.STUCK_PAUSE_MS
      ) {
        this.stopMomentum();
        clearTimeout(this.resumeTimeout);
        this.autoplayPaused = false;
        this.pausedAt = null;
      }

      const shouldBeLooping =
        this.enabled &&
        !this.reduceMotion &&
        !this.autoplayPaused &&
        !this.isDragging &&
        !this.pointerActive;
      if (shouldBeLooping && !this.intervalId) {
        this.startLoop();
      }
    }, 2500);
  }

  stopWatchdog() {
    if (this.watchdogId) {
      clearInterval(this.watchdogId);
      this.watchdogId = null;
    }
  }

  tick() {
    if (this.autoplayPaused || this.isDragging || !this.enabled) {
      this.stopLoop();
      return;
    }
    const now = Date.now();
    const deltaSeconds = this.lastTickAt ? (now - this.lastTickAt) / 1000 : this.TICK_MS / 1000;
    this.lastTickAt = now;
    this.track.scrollLeft += this.SPEED_PX_PER_SEC * deltaSeconds;
    this.normalizeLoop();
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.stopLoop();
    } else if (this.enabled && !this.autoplayPaused && !this.isDragging && !this.reduceMotion) {
      this.startLoop();
    }
  }
}

if (!customElements.get('skin-concern-marquee')) {
  customElements.define('skin-concern-marquee', SkinConcernMarquee);
}
